import { Observable } from 'rxjs';

import { getErrorMessage } from '../../plugins/errorMessage';

import { apiUrl, buildErrorMessage, getResult } from './apiQuery.helper';

export class ApiQuery {
  private readonly baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? apiUrl();
    if (this.baseUrl.endsWith('/')) {
      this.baseUrl = this.baseUrl.substring(0, this.baseUrl.length - 1);
    }
  }

  public async get<R>(
    endpointPath: string,
    requestInit?: RequestInit
  ): Promise<R> {
    const endpoint = this.getEndpointUrl(endpointPath);
    try {
      const response = await fetch(endpoint, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/plain, */*',
        },
        ...requestInit,
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(
          `[REST API GET Error]: Request failed with status ${response.status}`
        );
      }

      return await getResult<R>(response);
    } catch (error) {
      const errorMessage = buildErrorMessage([
        '[REST API GET Error]: Could not fetch data on Get request',
        getErrorMessage(error),
        `Endpoint: ${endpoint}`,
      ]);

      throw new Error(errorMessage);
    }
  }

  public getObservable<R>(
    endpointPath: string,
    headers?: HeadersInit
  ): Observable<R> {
    return this.asObservable<R>(async (abortSignal) =>
      this.get<R>(endpointPath, {
        headers,
        signal: abortSignal,
      })
    );
  }

  public async post<P, R>(
    endpointPath: string,
    payload?: P,
    requestInit?: RequestInit
  ): Promise<R> {
    const endpoint = this.getEndpointUrl(endpointPath);

    try {
      const response = await fetch(endpoint, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/plain, */*',
        },
        ...requestInit,
        method: 'POST',
        body: payload ? JSON.stringify(payload) : null,
      });

      if (!response.ok) {
        throw new Error(
          `[REST API POST Error]: Request failed with status ${response.status}`
        );
      }

      return await getResult<R>(response);
    } catch (error) {
      const errorMessage = buildErrorMessage([
        '[REST API POST Error]: Could not fetch data on POST request',
        getErrorMessage(error),
        `Endpoint ${endpoint}`,
      ]);

      throw new Error(errorMessage);
    }
  }

  public postObservable<P, R>(
    endpointPath: string,
    payload?: P
  ): Observable<R> {
    return this.asObservable<R>(async (abortSignal) =>
      this.post<P, R>(endpointPath, payload, {
        method: 'POST',
        signal: abortSignal,
      })
    );
  }

  public getEndpointUrl(endpointPath: string): string {
    return (
      this.baseUrl + (endpointPath.startsWith('/') ? '' : '/') + endpointPath
    );
  }

  private asObservable<T>(
    taskProducer: (abortSignal: AbortSignal) => Promise<T>
  ): Observable<T> {
    return new Observable<T>((observer) => {
      const controller = new AbortController();
      const signal = controller.signal;
      let disposed = false;
      let finished = false;

      taskProducer(signal).then(
        (result) => {
          if (!disposed) {
            finished = true;
            observer.next(result);
            observer.complete();
          }
        },
        (error) => {
          observer.error(error);
        }
      );

      return () => {
        if (disposed || finished) {
          return;
        }

        disposed = true;
        controller.abort();
      };
    });
  }
}
